import * as React from 'react';
import {useState, useCallback, useEffect} from 'react';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import HomeIcon from '@mui/icons-material/Home';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import Settings from '@mui/icons-material/Settings';
import TaskIcon from '@mui/icons-material/Task';
import {useInactivityRefresh} from '../hooks/useInactivityRefresh';

import PersonIcon from '@mui/icons-material/Person';

import GroupsIcon from '@mui/icons-material/Groups';
import DrawerLeft from './Drawer/Drawer';
import AppBar from './AppBar/AppBar';
import * as serviceWorker from '../serviceWorkerRegistration';
import CloseIcon from '@mui/icons-material/Close';

import MainPages from './MainPages/MainPages';
import {Alert, Button, Icon, IconButton, Snackbar} from '@mui/material';
import MaintenanceScreen from './MaintenanceScreen/MaintenanceScreen';
import UserContext from '../Contexts/UserContext/UserContext';
import {fetchMaintenanceStatus} from '../services/maintenance';
import ServiceWorkerUI from './ServiceWorkerUI';
import {
  Aod,
  BeachAccessOutlined,
  Discount,
  FolderDelete,
  LineAxis,
  Receipt as ReceiptIcon,
  SettingsSystemDaydream,
  TapAndPlay,
  TimeToLeave,
} from '@mui/icons-material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import {useLocation} from 'react-router-dom';

const navigationLinks_Standard = [
  {
    name: 'Startseite',
    link: '/',
    icon: <HomeIcon />,
    authNeeded: false,
  },
  // {
  //   name: "Meine Dokumente",
  //   link: "/documents",
  //   icon: <InboxIcon />,
  //   authNeeded: false
  // },
];
const navigationLinks_Admin = [
  {
    name: 'Usermanagement',
    link: '/usermanagement',
    icon: <PeopleAltIcon />,
    authNeeded: true,
  },
  {
    name: 'Nutzergruppen',
    link: '/usergroupmanagement',
    icon: <GroupsIcon />,
    authNeeded: true,
  },
  {
    name: 'Dokumentenverwaltung',
    link: '/document_management',
    icon: <InboxIcon />,
    authNeeded: true,
  },
  {
    name: 'Hausverwaltung',
    link: '/hausverwaltung',
    icon: <HolidayVillageIcon />,
    authNeeded: true,
  },
  {
    name: 'Speicherplatz',
    link: '/cloud_storage',
    icon: <SettingsSystemDaydream />,
    authNeeded: true,
  },
  {
    name: 'Sync-Geräte',
    link: '/sync_devices',
    icon: <CloudSyncIcon />,
    authNeeded: true,
  },
  {
    name: 'Pflege der Werbung',
    link: '/configure_ads',
    icon: <Discount />,
    authNeeded: true,
  },
  {
    name: 'Pflege Datenschutzerklärung',
    link: '/privacy-policy-edit',
    icon: <AdminPanelSettingsIcon />,
    authNeeded: true,
  },
];

const navigationLinks_Userspecific = [
  {
    name: 'FAQs',
    link: '/faqs',
    icon: <HelpCenterIcon />,
    authNeeded: false,
  },
  {
    name: 'Mein Account',
    link: '/user_options',
    icon: <PersonIcon />,
    authNeeded: true,
  },
  {
    name: 'Allgemeine Einstellungen',
    link: '/settings',
    icon: <Settings />,
    authNeeded: true,
  },
];

const navigationLinks_Special = [
  {
    name: 'Logs',
    link: '/logs',
    icon: <AnalyticsIcon />,
    authNeeded: true,
  },
];

const navigationLinks_Zeiterfassung = [
  {
    name: 'Zeiterfassung - Übersicht',
    link: '/zeiterfassung/dashboard',
    icon: <LineAxis />,
    authNeeded: true,
  },
  {
    name: 'mobile Zeiterfassung',
    link: '/zeiterfassung',
    icon: <Aod />,
    authNeeded: true,
  },
  {
    name: 'Stempeln',
    link: '/zeiterfassung/stempeln',
    icon: <TapAndPlay />,
    authNeeded: true,
  },
  {
    name: 'Urlaubsantrag',
    link: '/zeiterfassung/urlaub',
    icon: <BeachAccessOutlined />,
    authNeeded: true,
  },
];

const navigationLinks_Aufgaben = [
  {
    name: 'Aufgaben',
    link: '/aufgaben',
    icon: <TaskIcon />,
    allowedRoles: ['Administrator', 'Admin Aufgaben', 'Aufgaben Mitarbeiter'],
  },
  {
    name: 'Rapporte & Rechnung',
    link: '/rapporte-rechnung',
    icon: <ReceiptIcon />,
    allowedRoles: ['Administrator', 'Administrator_Rechnung'],
  },
];

interface IApplicationProps {
  children?: React.ReactNode;
}

export default function Application(props: IApplicationProps) {
  const location = useLocation();
  const isAuthPage = location.pathname.startsWith('/auth');
  const {maintenanceBlocked} = React.useContext(UserContext);
  const [maintenanceInfo, setMaintenanceInfo] = useState(false);

  // Periodically validate SuperTokens session every 5 minutes
  useInactivityRefresh();

  // On the login page the backend gate cannot apply yet (no session) -
  // show an informational banner instead so admins still can sign in.
  useEffect(() => {
    if (isAuthPage) {
      fetchMaintenanceStatus().then(setMaintenanceInfo);
    }
  }, [isAuthPage]);

  if (maintenanceBlocked && !isAuthPage) {
    return <MaintenanceScreen />;
  }

  return (
    // <ThemeProvider theme={theme.palette.main}>
    <>
        <Box sx={{display: 'flex'}}>
          <CssBaseline />
          {isAuthPage && maintenanceInfo && (
            <Alert
              severity='warning'
              sx={{position: 'fixed', top: 0, left: 0, right: 0, borderRadius: 0, zIndex: (theme) => theme.zIndex.drawer + 1}}>
              Wartungsmodus aktiv — die Anmeldung ist zurzeit nur für Administratoren möglich.
            </Alert>
          )}
          {!isAuthPage && <AppBar />}
          {!isAuthPage && (
            <DrawerLeft
              navigationLinks_Admin={navigationLinks_Admin}
              navigationLinks_Standard={navigationLinks_Standard}
              navigationLinks_Userspecific={navigationLinks_Userspecific}
              navigationLinks_Special={navigationLinks_Special}
              navigationLinks_Zeiterfassung={navigationLinks_Zeiterfassung}
              navigationLinks_Aufgaben={navigationLinks_Aufgaben}></DrawerLeft>
          )}
          <MainPages></MainPages>

          {props.children}
        </Box>
    </>
    // </ThemeProvider>
  );
}
